import React, { useRef, useMemo, Suspense } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import { Float, MeshDistortMaterial } from "@react-three/drei";
import * as THREE from "three";

function GoldDiamond({ scale = 1 }) {
    const meshRef = useRef();
    const outerRef = useRef();
    const outerRef2 = useRef();

    useFrame((state, delta) => {
        if (meshRef.current) {
            meshRef.current.rotation.y += delta * 0.6;
            meshRef.current.rotation.x = Math.sin(state.clock.elapsedTime * 0.3) * 0.25;
        }
        if (outerRef.current) {
            outerRef.current.rotation.y -= delta * 0.2;
            outerRef.current.rotation.z += delta * 0.12;
        }
        if (outerRef2.current) {
            outerRef2.current.rotation.x += delta * 0.15;
            outerRef2.current.rotation.z -= delta * 0.08;
        }
    });

    return (
        <group scale={scale}>
            <mesh ref={meshRef} castShadow>
                <octahedronGeometry args={[1.8, 0]} />
                <meshPhysicalMaterial
                    color="#f4d789"
                    emissive="#8a6d2b"
                    emissiveIntensity={0.35}
                    metalness={1}
                    roughness={0.15}
                    clearcoat={1}
                    clearcoatRoughness={0.05}
                    reflectivity={1}
                />
            </mesh>
            <mesh ref={outerRef} scale={2.6}>
                <torusGeometry args={[1, 0.012, 16, 100]} />
                <meshBasicMaterial color="#e8c86a" />
            </mesh>
            <mesh ref={outerRef2} scale={3.0}>
                <torusGeometry args={[1, 0.006, 12, 100]} />
                <meshBasicMaterial color="#c9a961" transparent opacity={0.7} />
            </mesh>
        </group>
    );
}

function GoldParticles({ count = 220 }) {
    const meshRef = useRef();
    const dummy = useMemo(() => new THREE.Object3D(), []);
    const positions = useMemo(() => {
        const arr = [];
        for (let i = 0; i < count; i++) {
            const r = 3 + Math.random() * 5;
            const theta = Math.random() * Math.PI * 2;
            const phi = Math.acos(2 * Math.random() - 1);
            arr.push({
                x: r * Math.sin(phi) * Math.cos(theta),
                y: r * Math.sin(phi) * Math.sin(theta),
                z: r * Math.cos(phi),
                speed: 0.15 + Math.random() * 0.5,
                offset: Math.random() * Math.PI * 2,
                scale: 0.02 + Math.random() * 0.05,
            });
        }
        return arr;
    }, [count]);

    useFrame((state) => {
        if (!meshRef.current) return;
        const t = state.clock.elapsedTime;
        positions.forEach((p, i) => {
            const angle = t * p.speed + p.offset;
            dummy.position.set(
                p.x + Math.sin(angle) * 0.4,
                p.y + Math.cos(angle * 0.8) * 0.4,
                p.z + Math.sin(angle * 0.6) * 0.4,
            );
            dummy.scale.setScalar(p.scale);
            dummy.updateMatrix();
            meshRef.current.setMatrixAt(i, dummy.matrix);
        });
        meshRef.current.instanceMatrix.needsUpdate = true;
        meshRef.current.rotation.y = t * 0.05;
    });

    return (
        <instancedMesh ref={meshRef} args={[null, null, count]}>
            <sphereGeometry args={[1, 8, 8]} />
            <meshBasicMaterial color="#e8c86a" transparent opacity={0.9} />
        </instancedMesh>
    );
}

function MarblePlate() {
    const ref = useRef();
    useFrame((state, delta) => {
        if (ref.current) ref.current.rotation.z += delta * 0.06;
    });
    return (
        <mesh ref={ref} position={[0, -2.6, 0]} rotation={[-Math.PI / 2, 0, 0]}>
            <cylinderGeometry args={[2.8, 2.8, 0.15, 64]} />
            <MeshDistortMaterial
                color="#f7f1e3"
                metalness={0.7}
                roughness={0.15}
                distort={0.06}
                speed={1.5}
                emissive="#c9a961"
                emissiveIntensity={0.05}
            />
        </mesh>
    );
}

export default function ThreeScene({ variant = "hero" }) {
    const cameraPos = variant === "hero" ? [0, 0.3, 9] : [0, 0, 10];
    const scale = variant === "hero" ? 0.7 : 0.5;
    return (
        <Canvas
            dpr={[1, 1.8]}
            camera={{ position: cameraPos, fov: 45 }}
            gl={{ antialias: true, alpha: true }}
            style={{ background: "transparent" }}
        >
            <Suspense fallback={null}>
                <ambientLight intensity={0.6} />
                <directionalLight position={[5, 5, 5]} intensity={1.6} color="#fff5d0" />
                <directionalLight position={[-5, -3, -5]} intensity={0.8} color="#c9a961" />
                <pointLight position={[0, 3, 3]} intensity={2.5} color="#e8c86a" />
                <pointLight position={[3, -2, 2]} intensity={1.2} color="#f4d789" />

                <Float speed={1.4} rotationIntensity={0.6} floatIntensity={0.9}>
                    <GoldDiamond scale={scale} />
                </Float>

                {variant === "hero" && <MarblePlate />}
                <GoldParticles count={variant === "hero" ? 260 : 150} />
            </Suspense>
        </Canvas>
    );
}
